import { BaseService } from "./BaseService";
import api from "../Interceptors/Interceptor";
import { AxiosResponse } from "axios";
import ApiResponse from "../Models/ApiResponse";
import { CustomerStylingModel, IBrowserBrandingModel } from "./customerStyleService";
import secureLocalStorage from 'react-secure-storage';
import SecurityUserResponse from '../Models/Authentication/SecurityUserResponse';

export interface ICrudCustomerModel {
    uuid: string
    name: string
    style: string
    applicationUrl: string
    totalUsers: number
    totalLinkCount: number
    totalPermissions: number
    totalDashboards: number
    hasOwnGcpProject: boolean
    gcpProjectId: string
    active: boolean
}

export interface ICustomerModel {
    uuid: string
    name: string
    style: string
    applicationUrl: string
    totalUsers: number
    totalLinkCount: number
    totalPermissions: number
    totalDashboards: number
    hasOwnGcpProject: boolean
    gcpProjectId: string
    active: boolean
    parentCustomer?: { uuid?: string } | string | null
}

export interface CustomerLogoModel {
    uuid?: string;
    logo: string;
}
export interface CustomerLogoWideModel {
    uuid?: string;
    logo: string;
}

export class CustomerService extends BaseService<ICustomerModel, ICrudCustomerModel> {

    protected modelUrl(): string {
        return "security-customer";
    }

    public async getCustomerStyling(uuid: string): Promise<AxiosResponse<ApiResponse<CustomerStylingModel[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${uuid}/customer-style`, {});
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async getCustomerLogo(uuid: string): Promise<AxiosResponse<ApiResponse<CustomerLogoModel>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${uuid}/customer-logo`, {});
            return response;
        } catch (error) {
            console.error('Error fetching customer logo:', error);

            const mockResponse = {
                data: {
                    code: "APIRQCL",
                    message: "No logo found, using default",
                    data: null
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: {} as any,
            } as unknown as AxiosResponse<ApiResponse<CustomerLogoModel>>;

            return Promise.resolve(mockResponse);
        }
    }
    public async saveCustomerLogo(customerUuid: string, logoData: CustomerLogoModel, alreadyHasLogo: boolean): Promise<AxiosResponse<ApiResponse<any>>> {
        if (!logoData.logo) {
            throw new Error("Logo cannot be null.");
        }

        if (alreadyHasLogo) {
            return api.put(`v1/${this.modelUrl()}/${customerUuid}/update-logo`, {
                logo: logoData.logo
            });
        } else {
            return api.post(`v1/${this.modelUrl()}/create-for-customer/${customerUuid}`, {
                logo: logoData.logo
            });
        }
    }
    public async deleteCustomerLogo(uuid: string): Promise<AxiosResponse<ApiResponse<any>>> {
        if (!uuid) {
            throw new Error("Error deleting logo.");
        }

        try {
            return await api.delete(`v1/${this.modelUrl()}/${uuid}/logo`);
        } catch (error) {
            throw error;
        }
    }

    public async getCustomerLogoWide(uuid: string): Promise<AxiosResponse<ApiResponse<CustomerLogoWideModel>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${uuid}/customer-logo-wide`, {});
            return response;
        } catch (error) {
            console.error('Error fetching customer logo Wide:', error);

            const mockResponse = {
                data: {
                    code: "APIRQCL",
                    message: "No logo Wide found, using default",
                    data: null
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: {} as any,
            } as unknown as AxiosResponse<ApiResponse<CustomerLogoWideModel>>;

            return Promise.resolve(mockResponse);
        }
    }

    public async saveCustomerLogoWide(customerUuid: string, logoData: CustomerLogoWideModel, alreadyHasLogoWide: boolean): Promise<AxiosResponse<ApiResponse<any>>> {
        if (!logoData.logo) {
            throw new Error("Logo cannot be null.");
        }

        if (alreadyHasLogoWide) {
            return api.put(`v1/${this.modelUrl()}/${customerUuid}/update-logo-wide`, {
                logoWide: logoData.logo
            });
        } else {
            return api.post(`v1/${this.modelUrl()}/create-for-customer-wide/${customerUuid}`, {
                logoWide: logoData.logo
            });
        }
    }
    public async deleteCustomerLogoWide(uuid: string): Promise<AxiosResponse<ApiResponse<any>>> {
        if (!uuid) {
            throw new Error("Error deleting logo.");
        }

        try {
            return await api.delete(`v1/${this.modelUrl()}/${uuid}/logo-wide`);
        } catch (error) {
            console.error('Error deleting logo Wide:', error);
            throw error;
        }
    }
    public async getBrowserBranding(uuid: string): Promise<AxiosResponse<ApiResponse<IBrowserBrandingModel>>> {
        return await api.get(`v1/${this.modelUrl()}/${uuid}/browser-branding`, {});
    }

    public async saveBrowserBranding(
        uuid: string,
        branding: IBrowserBrandingModel,
    ): Promise<AxiosResponse<ApiResponse<IBrowserBrandingModel>>> {
        return await api.put(`v1/${this.modelUrl()}/${uuid}/browser-branding`, branding);
    }

    public async getWhiteLabel(uuid: string): Promise<AxiosResponse<ApiResponse<{ whiteLabel?: boolean; editable?: boolean }>>> {
        return await api.get(`v1/${this.modelUrl()}/${uuid}/white-label`, {});
    }

    public async saveWhiteLabel(
        uuid: string,
        whiteLabel: boolean,
    ): Promise<AxiosResponse<ApiResponse<{ whiteLabel?: boolean; editable?: boolean }>>> {
        return await api.put(`v1/${this.modelUrl()}/${uuid}/white-label`, { whiteLabel });
    }

    /** Customer-specific CSS for the pre-login pages (parameter CUSTOM_STYLING); null = none. */
    public async getCustomStyling(uuid: string): Promise<AxiosResponse<ApiResponse<{ customStyling?: string | null }>>> {
        return await api.get(`v1/${this.modelUrl()}/${uuid}/custom-styling`, {});
    }

    public async saveCustomStyling(
        uuid: string,
        customStyling: string | null,
    ): Promise<AxiosResponse<ApiResponse<{ customStyling?: string | null }>>> {
        return await api.put(`v1/${this.modelUrl()}/${uuid}/custom-styling`, { customStyling });
    }

    public async postAdminCreateCustomer(data: any): Promise<AxiosResponse<ApiResponse<{ uuid?: string }>>> {
        try {
            const response = await api.post(`v1/${this.modelUrl()}/harry-wizard`, data, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response;
        } catch (error) {
            console.error('Error creating customer:', error);
            throw error;
        }
    }

    public async getAssignedPermission(uuid: string): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${uuid}/permission`, {});
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }


    public async getAvailablePermissions(uuid: string): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${uuid}/available-permissions`, {});
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async saveCustomerPermission(customer: any, permissions: any[]): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.put(`v1/${this.modelUrl()}/${customer.uuid}/permission`, permissions);
            return response;
        } catch (error) {
            console.error('Error saving role permissions:', error);
            throw error;
        }
    }

    public getCurrentUserCustomerUuid() {
        const userAuth = secureLocalStorage.getItem('user') as string;
        if (userAuth) {
            if (typeof userAuth === 'string') {
                let securityUserResponse = JSON.parse(userAuth) as SecurityUserResponse;
                return securityUserResponse.customerUuid;
            }
        }
        return undefined;
    }

}